import api from './api';

/**
 * Превью сгенерированных сайтов.
 *
 * Адрес превью выдаёт сервер в ответе POST /api/generation/preview-v2/:id/access (`previewUrl`):
 * абсолютный URL на отдельном origin (https://wp-preview.3a-fb.info/s/<sid>/index.html) или,
 * в dev без отдельного домена, относительный /api/generation/preview-v2/<id>/index.html.
 * Если сервер ещё отвечает старым `{ success: true }` без previewUrl, используется прежний адрес.
 */

const PREVIEW_V2_PATH_RE = /\/api\/generation\/preview-v2\/([^/?#]+)/;
const ABSOLUTE_URL_RE = /^([a-z][a-z\d+.-]*:)?\/\//i;

export interface PreviewAccessOptions {
  /** Отладочный режим превью; сервер учитывает его только для администратора. */
  debug?: boolean;
}

const parseHttpUrl = (value: string, base: string): URL | null => {
  try {
    const parsed = new URL(value, base);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed : null;
  } catch {
    return null;
  }
};

/** generationId из старой ссылки вида /api/generation/preview-v2/<id>[/...]. */
export const extractPreviewGenerationId = (url?: string | null): string | null => {
  if (!url) return null;
  const match = url.match(PREVIEW_V2_PATH_RE);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/** Прежний адрес превью — только для fallback, когда сервер не вернул previewUrl. */
export const buildLegacyPreviewUrl = (generationId: string, debug = false): string =>
  `/api/generation/preview-v2/${encodeURIComponent(generationId)}/index.html${debug ? '?debug=1' : ''}`;

/**
 * Выдаёт доступ к превью и возвращает адрес для открытия.
 * debug передаётся в теле запроса, а не в query.
 */
export const requestPreviewAccess = async (
  generationId: string,
  options: PreviewAccessOptions = {}
): Promise<string> => {
  const debug = Boolean(options.debug);
  const accessPath = `/api/generation/preview-v2/${encodeURIComponent(generationId)}/access`;
  const response = debug ? await api.post(accessPath, { debug: true }) : await api.post(accessPath);
  const rawPreviewUrl: unknown = response?.data?.previewUrl;

  if (typeof rawPreviewUrl === 'string' && rawPreviewUrl.trim()) {
    const previewUrl = rawPreviewUrl.trim();
    if (!parseHttpUrl(previewUrl, window.location.origin)) {
      throw new Error('Сервер вернул некорректную ссылку превью');
    }
    return previewUrl;
  }

  return buildLegacyPreviewUrl(generationId, debug);
};

/**
 * Открывает пустую вкладку синхронно в обработчике клика (иначе после await её блокирует
 * блокировщик всплывающих окон) и обнуляет opener, чтобы страница превью не получила ссылку на приложение.
 */
export const openPendingPreviewWindow = (): Window | null => {
  const previewWindow = window.open('about:blank', '_blank');
  if (!previewWindow) return null;
  try {
    previewWindow.opener = null;
  } catch {
    // opener может быть недоступен для записи — вкладка всё равно открыта
  }
  return previewWindow;
};

export const navigatePreviewWindow = (previewWindow: Window, previewUrl: string): void => {
  previewWindow.location.replace(previewUrl);
};

/**
 * Публичная ссылка из ответа /public-link: абсолютный URL (отдельный origin превью) остаётся как есть,
 * относительный (старый формат) достраивается от baseURL API.
 */
export const resolvePublicPreviewUrl = (publicUrl: unknown, base: string): string | null => {
  if (typeof publicUrl !== 'string' || !publicUrl.trim()) return null;
  const value = publicUrl.trim();
  const resolved = parseHttpUrl(value, base);
  if (!resolved) return null;
  if (ABSOLUTE_URL_RE.test(value)) return resolved.href;

  if (/\/public-preview\/[^/]+\/?$/.test(resolved.pathname)) {
    resolved.pathname = `${resolved.pathname.replace(/\/$/, '')}/index.html`;
  }
  return resolved.href;
};
